import { ConnectResult, create, createPromiseClient } from '@/connect';
import {
  CreateConfigRequestSchema,
  CreateConfigResponse,
  DeleteConfigRequestSchema,
  DeleteConfigResponse,
  GetConfigsByNameRequestSchema,
  ModifyConfigRequestSchema,
  ModifyConfigResponse,
  MoveConfigRequestSchema,
  MoveConfigResponse,
  UICustomizationService,
} from '@/connect/gen/modules/apiengine/services/uicustomization/uicustomization_pb';
import type { SavedView } from '@/modules/saved-views/types';
import type { JsonObject } from '@bufbuild/protobuf';
import { omit } from 'lodash-es';

export const uiCustomizationService = new (class {
  public readonly promiseClient = createPromiseClient(UICustomizationService);

  public async getSavedViews<T>(name: string): Promise<Array<SavedView<T>>> {
    const request = create(GetConfigsByNameRequestSchema, { name });
    const result = await this.promiseClient.getConfigsByName(request);
    if (!result.success) return [];

    const views = result.data.configs.map<SavedView<T> | null>((item) => {
      if (!item.config) return null;
      const { config } = item;
      return {
        id: item.id,
        filterState: config.filterState as T,
        title: config.title as string,
        description: config.description as string,
        visibility: item.visibility,
        isOwner: item.isOwner,
      };
    });

    return views.filter((view): view is SavedView<T> => view !== null);
  }

  public async createSavedView<T>(
    name: string,
    position: number,
    view: SavedView<T>
  ): Promise<ConnectResult<CreateConfigResponse>> {
    const request = create(CreateConfigRequestSchema, {
      name,
      config: omit(view, 'visibility') as JsonObject,
      visibility: view.visibility,
      position,
    });
    const result = await this.promiseClient.createConfig(request);
    return result as ConnectResult<CreateConfigResponse>;
  }

  public async modifySavedView<T>(
    viewId: string,
    view: SavedView<T>
  ): Promise<ConnectResult<ModifyConfigResponse>> {
    const request = create(ModifyConfigRequestSchema, {
      configId: viewId,
      config: omit(view, ['id']) as JsonObject,
      visibility: view.visibility,
    });
    const result = await this.promiseClient.modifyConfig(request);
    return result as ConnectResult<ModifyConfigResponse>;
  }

  public async moveSavedView(
    viewId: string,
    position: number
  ): Promise<ConnectResult<MoveConfigResponse>> {
    const request = create(MoveConfigRequestSchema, {
      configId: viewId,
      newPosition: position,
    });
    const result = await this.promiseClient.moveConfig(request);
    return result as ConnectResult<MoveConfigResponse>;
  }

  public async deleteSavedView(configId: string): Promise<ConnectResult<DeleteConfigResponse>> {
    const request = create(DeleteConfigRequestSchema, { configId });
    const result = await this.promiseClient.deleteConfig(request);
    return result as ConnectResult<DeleteConfigResponse>;
  }
})();
